/**
 * audioIndexedDB.js — IndexedDB crash recovery for pre-recorded audio chunks.
 *
 * Stores audio blobs locally so that if the browser closes mid-recording,
 * orphaned chunks can be recovered on next page load.
 *
 * DB: featherglass-audio-chunks
 * Store: chunks (keyPath: auto-increment, index on documentId)
 *
 * See: docs/features/integrations/deepgram-prerecorded.md
 */

const DB_NAME = 'featherglass-audio-chunks';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('documentId', 'documentId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save a single audio chunk blob to IndexedDB.
 * @param {string} documentId - Firestore document ID for the session
 * @param {number} chunkIndex - Ordered chunk index
 * @param {Blob} blob - The audio blob
 */
export async function saveChunkToIDB(documentId, chunkIndex, blob) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.add({
      documentId,
      chunkIndex,
      blob,
      createdAt: Date.now(),
    });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.error('[audioIndexedDB] Failed to save chunk:', err);
  }
}

/**
 * Get all orphaned chunks grouped by documentId.
 * Used on page load to detect crash recovery candidates.
 * @returns {Promise<Object<string, Array<{ chunkIndex: number, blob: Blob, createdAt: number }>>>}
 */
export async function getOrphanedChunks() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const allRecords = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();

    // Group by documentId
    const grouped = {};
    for (const record of allRecords) {
      if (!grouped[record.documentId]) {
        grouped[record.documentId] = [];
      }
      grouped[record.documentId].push({
        chunkIndex: record.chunkIndex,
        blob: record.blob,
        createdAt: record.createdAt,
      });
    }

    // Sort each group by chunkIndex
    for (const docId of Object.keys(grouped)) {
      grouped[docId].sort((a, b) => a.chunkIndex - b.chunkIndex);
    }

    return grouped;
  } catch (err) {
    console.error('[audioIndexedDB] Failed to get orphaned chunks:', err);
    return {};
  }
}

/**
 * Get chunks for a specific document from IndexedDB.
 * Used for recovering failed rolling transcription chunks.
 * @param {string} documentId
 * @returns {Promise<Array<{ chunkIndex: number, blob: Blob, createdAt: number }>>}
 */
export async function getChunksForDocument(documentId) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('documentId');
    const chunks = await new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(documentId));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    db.close();

    // Sort by chunkIndex and return clean shape
    return chunks
      .map((r) => ({ chunkIndex: r.chunkIndex, blob: r.blob, createdAt: r.createdAt }))
      .sort((a, b) => a.chunkIndex - b.chunkIndex);
  } catch (err) {
    console.error('[audioIndexedDB] Failed to get chunks for document:', err);
    return [];
  }
}

/**
 * Clear all chunks for a given document (after successful upload).
 * @param {string} documentId
 */
export async function clearChunksForDocument(documentId) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('documentId');
    const request = index.openCursor(IDBKeyRange.only(documentId));

    await new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });

    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
    console.log(`[audioIndexedDB] Cleared chunks for document: ${documentId}`);
  } catch (err) {
    console.error('[audioIndexedDB] Failed to clear chunks:', err);
  }
}
